import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { getLoginUrl } from "@/const";
import { Loader2, LogOut, ShieldOff } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation } from "wouter";

const NAV: { href: string; label: string }[] = [
  { href: "/admin", label: "대시보드" },
  { href: "/admin/orders", label: "주문 관리" },
  { href: "/admin/products", label: "상품 가격" },
  { href: "/admin/members", label: "회원 관리" },
  { href: "/admin/inquiries", label: "문의 관리" },
  { href: "/admin/ai-debug", label: "AI Debug" },
];

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user, loading, isAuthenticated, logout } = useAuth();
  const [location] = useLocation();

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) window.location.href = getLoginUrl();
  }, [loading, isAuthenticated]);

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center p-10">
        <div className="max-w-md text-center">
          <ShieldOff className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
          <div className="font-serif text-2xl text-primary mb-1">
            관리자 권한이 필요합니다
          </div>
          <p className="text-sm text-muted-foreground">
            이 페이지는 관리자만 접근할 수 있습니다.
          </p>
          <Button asChild className="mt-5">
            <Link href="/">홈으로</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-[240px_1fr] bg-muted/30">
      <aside className="hidden md:flex flex-col border-r border-border bg-sidebar text-sidebar-foreground">
        <div className="px-5 py-5 border-b border-sidebar-border">
          <Link
            href="/"
            className="font-serif text-lg text-sidebar-primary block"
          >
            Oil Print Studio
          </Link>
          <div className="text-xs text-muted-foreground mt-0.5">
            Admin Console
          </div>
        </div>
        <nav className="p-3 flex-1 space-y-1">
          {NAV.map((n) => {
            const active = location === n.href;
            return (
              <Link
                key={n.href}
                href={n.href}
                className={
                  "block rounded-sm px-3 py-2 text-sm transition-colors " +
                  (active
                    ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                    : "text-muted-foreground hover:bg-sidebar-accent/60")
                }
              >
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-sidebar-border">
          <div className="text-xs text-muted-foreground px-3 py-1">
            {user?.email ?? user?.name}
          </div>
          <button
            onClick={() => logout()}
            className="w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-sidebar-accent/60 rounded-sm inline-flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            로그아웃
          </button>
        </div>
      </aside>
      <div className="min-w-0">
        <div className="md:hidden border-b bg-sidebar p-3">
          <div className="flex gap-2 overflow-x-auto">
            {NAV.map((n) => {
              const active = location === n.href;
              return (
                <Link
                  key={n.href}
                  href={n.href}
                  className={
                    "text-xs px-3 py-1.5 rounded-full whitespace-nowrap " +
                    (active
                      ? "bg-primary text-primary-foreground"
                      : "bg-sidebar-accent text-sidebar-accent-foreground")
                  }
                >
                  {n.label}
                </Link>
              );
            })}
          </div>
        </div>
        <div className="p-5 md:p-10">{children}</div>
      </div>
    </div>
  );
}
